// Charts/RnCharts/PieChart.tsx
import React from 'react';
import { View, Text } from 'react-native';
import Svg, { Path, Text as SvgText } from 'react-native-svg';
import { ChartConfig } from '../types';

interface PieChartData {
  name: string;
  value: number;
  color: string;
  legendFontColor: string;
  legendFontSize: number;
}

interface PieChartProps {
  data: PieChartData[];
  width: number;
  height: number;
  chartConfig: ChartConfig;
  accessor: string;
  backgroundColor?: string;
  paddingLeft?: string;
  center?: [number, number];
  absolute?: boolean;
  hasLegend?: boolean;
  onDataPointClick?: (data: any, index: number) => void;
  isZeroValues?: boolean;
}

const PieChart: React.FC<PieChartProps> = ({
  data,
  width,
  height,
  chartConfig,
  accessor,
  center = [0, 0],
  hasLegend = true,
  onDataPointClick,
  isZeroValues = false,
}) => {
  const radius = Math.min(width, height) / 4;
  const centerX = width / 2 + center[0];
  const centerY = height / 2 + center[1];

  const total = data.reduce((sum, item) => sum + item.value, 0) || 1;
  let currentAngle = -Math.PI / 2; // Start from top

  const createArcPath = (
    startAngle: number,
    endAngle: number,
    innerRadius = 0
  ) => {
    const x1 = centerX + Math.cos(startAngle) * radius;
    const y1 = centerY + Math.sin(startAngle) * radius;
    const x2 = centerX + Math.cos(endAngle) * radius;
    const y2 = centerY + Math.sin(endAngle) * radius;

    const largeArcFlag = endAngle - startAngle <= Math.PI ? '0' : '1';

    return [
      'M',
      centerX,
      centerY,
      'L',
      x1,
      y1,
      'A',
      radius,
      radius,
      0,
      largeArcFlag,
      1,
      x2,
      y2,
      'Z',
    ].join(' ');
  };

  return (
    <View style={{ backgroundColor: chartConfig.backgroundColor }}>
      <Svg width={width} height={height}>
        {data.map((item, index) => {
          const angle = isZeroValues
            ? (2 * Math.PI) / data.length
            : (item.value / total) * 2 * Math.PI;
          const endAngle = currentAngle + angle;
          const path = createArcPath(currentAngle, endAngle);

          const result = (
            <Path
              key={index}
              d={path}
              fill={item.color}
              onPress={() => onDataPointClick?.(item, index)}
            />
          );

          currentAngle = endAngle;
          return result;
        })}
      </Svg>

      {hasLegend && (
        <View style={{ paddingHorizontal: 20, paddingTop: 10 }}>
          {data.map((item, index) => (
            <View
              key={index}
              style={{
                flexDirection: 'row',
                alignItems: 'center',
                marginBottom: 5,
              }}
            >
              <View
                style={{
                  width: 12,
                  height: 12,
                  backgroundColor: item.color,
                  marginRight: 8,
                  borderRadius: 2,
                }}
              />
              <Text
                style={{
                  color: item.legendFontColor,
                  fontSize: item.legendFontSize,
                }}
              >
                {item.name}: {item.value}
              </Text>
            </View>
          ))}
        </View>
      )}
    </View>
  );
};

export default PieChart;
